import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import './index.css'

interface ValueChartComponentProps {
  title: string
  value: number | string
  units?: string // Optional units prop
  avatar?: React.ReactNode
  data_testid?: string
  cardBackground?: string // This will now be the icon's background color
  textColor?: string // This will now be the icon's color
}

/**
 * ValueChartComponent: Renders a polished card displaying a key metric.
 * This modern design uses a clean, two-tone approach for better visual hierarchy.
 */
const ValueChartComponent: React.FC<ValueChartComponentProps> = ({
  title,
  value,
  units,
  avatar,
  data_testid,
  cardBackground,
  textColor,
}) => {
  return (
    // The main card is now white with a subtle border and shadow for a cleaner look
    <Card
      sx={{
        borderRadius: '16px', // Softer, more modern corners
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)', // A softer shadow
        border: '1px solid #E5E7EB', // Subtle border
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
        {/* Top section with title and icon */}
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
          <Typography sx={{ color: '#4B5563', fontWeight: 600 }}>{title}</Typography>
          {avatar && (
            // The icon is now inside a colored circle, providing a nice accent
            <div className='p-2 rounded-full' style={{ backgroundColor: cardBackground }}>
              {/* The icon itself receives the text color */}
              {avatar}
            </div>
          )}
        </Stack>

        {/* Spacer to push the value to the bottom, ensuring alignment across cards */}
        <div style={{ flexGrow: 1 }} />

        {/* Bottom section with the main value */}
        <Typography
          variant='h3'
          data-testid={data_testid}
          sx={{ color: '#111827', fontWeight: 'bold', lineHeight: 1.2 }}
        >
          {value}
          {units && (
            <Typography
              component='span'
              sx={{
                fontSize: '1.25rem',
                fontWeight: 500,
                color: '#6B7281',
                marginLeft: '4px',
                whiteSpace: 'nowrap',
              }}
            >
              {units}
            </Typography>
          )}
        </Typography>
      </CardContent>
    </Card>
  )
}

export default ValueChartComponent
